/**
 * Yeap Site Builder — customer console API (/api/user/sites/*). Spec: sitebuilder/SPEC.md §7, ops: api/docs/SITES.md.
 * Every call resolves to body.data (or the body when it has no data key); failures throw
 * { error, status, toasted, body } (apiClient already toasts most 4xx/5xx; toastErr only fills the gaps).
 */
import apiClient from '../../../shared/services/apiClient';
import { t as tt } from '../../../shared/i18n';

export const BASE = '/api/user/sites';

const unwrap = (res) => {
    const body = res?.data;
    if (body && typeof body === 'object' && !Array.isArray(body)) {
        if (body.success === false) {
            // eslint-disable-next-line no-throw-literal
            throw { error: body.error || body.message, status: res?.status, body };
        }
        if ('data' in body) return body.data;
    }
    return body;
};

const call = async (p) => {
    try {
        return unwrap(await p);
    } catch (e) {
        if (e && !e.response && 'error' in e) throw e;
        const body = e?.response?.data || {};
        // eslint-disable-next-line no-throw-literal
        throw { error: body.error || body.message || e?.message, status: e?.response?.status, toasted: !!e?.toasted, body };
    }
};

const url = (path) => `${BASE}${path && !path.startsWith('/') ? '/' : ''}${path || ''}`;

export const sb = {
    get: (path, params, cfg) => call(apiClient.get(url(path), { ...(params ? { params } : {}), ...(cfg || {}) })),
    post: (path, data, cfg) => call(apiClient.post(url(path), data || {}, cfg)),
    put: (path, data, cfg) => call(apiClient.put(url(path), data || {}, cfg)),
    patch: (path, data, cfg) => call(apiClient.patch(url(path), data || {}, cfg)),
    del: (path, cfg) => call(apiClient.delete(url(path), cfg)),
    /** GET a file (CSV export) and save it in the browser */
    download: async (path, filename, params) => {
        const res = await apiClient.get(url(path), { params, responseType: 'blob', timeout: 120000 });
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    },
};

/** Per-site helper: site(id).get('/products') → /api/user/sites/<id>/products */
export const site = (id) => ({
    get: (p, params, cfg) => sb.get(`/${id}${p || ''}`, params, cfg),
    post: (p, d, cfg) => sb.post(`/${id}${p || ''}`, d, cfg),
    put: (p, d, cfg) => sb.put(`/${id}${p || ''}`, d, cfg),
    patch: (p, d, cfg) => sb.patch(`/${id}${p || ''}`, d, cfg),
    del: (p, cfg) => sb.del(`/${id}${p || ''}`, cfg),
    download: (p, filename, params) => sb.download(`/${id}${p || ''}`, filename, params),
});

/**
 * The API never receives files: ask for a presigned URL, PUT straight to storage, then the caller posts {key} to the
 * module's complete/import route. api = site(id). onProgress gets 0-100. Resolves to { key, name, size }.
 */
export const directUpload = async (api, file, purpose, { onProgress, name } = {}) => {
    const fileName = name || file.name || 'file';
    const u = await api.post('/uploads', { purpose, name: fileName, size: file.size, content_type: file.type || undefined });
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', u.url);
        Object.entries(u.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        if (onProgress) xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('network'));
        xhr.onabort = () => reject(new Error('aborted'));
        xhr.send(file);
    }).catch(() => {
        // eslint-disable-next-line no-throw-literal
        throw { error: tt('The upload failed. Try again'), status: 0, toasted: false, body: {} };
    });
    if (onProgress) onProgress(100);
    return { key: u.key, name: fileName, size: file.size };
};

/** quiet: no global error toast (for probing optional endpoints) */
export const QUIET = { skipGlobalErrorToast: true };

export default sb;
